/* app/providers/UnsavedChangesProvider.jsx — единый сторож «несохранённых изменений»
   (kitchen-ux A2: K-01, K-02, K-27; канон 01 §6.3, 02-IA §10.4 и §12).

   Решения — lib/unsavedGuard.js (чистое ядро с тестами); здесь диалог, история браузера
   и React-обвязка.

   API: const { guard, register } = useUnsaved();
        guard(action, { keys?, reason? }) → Promise<boolean>
          чисто — action сразу; грязно — диалог «Сохранить / Отбросить / Отмена»:
          «Сохранить» зовёт save формы и выполняет action только при успехе; reason —
          строка над списком, если переход начал не пользователь (соседняя вкладка).
        register(key, isDirty) — прежний контракт (снять грязь синхронно после записи).
   Форма: useUnsavedGuard(key, isDirty, { save, label }) — EntityEditor делает это за
   редакторы «Продукции»; ключ — уровень стека `type-id` (грязь принадлежит уровню).
   UnsavedScope aliveAt={(loc) => …} — где форма смонтирована: «назад» браузера / жест,
   уводящие с грязной формы, перехватываются ДО роутера — адрес возвращается (replace),
   спрашивается тот же диалог; согласились — переход туда, куда просил браузер (replace).
   Перехват — через RR.setPopstateGuard: роутер зовёт сторожа до синхронизации адреса
   (слушатель на window не годится: провайдер монтируется после загрузки сессии, и
   слушатель роутера успевал первым — React размонтировал форму раньше вопроса). Переход
   ОТКАТЫВАЕТСЯ по индексам записей (history.go), а не перезаписывается — история цела.
   Любой navigate() приложения (палитра Ctrl+K, «Редактировать в чате», ссылки
   уведомлений) — через RR.setNavigateGuard: тот же диалог до записи перехода.
   Решения — lib/unsavedGuard.js (installHistoryGuards, createGuardController). */

import React from 'react';
import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import {
  CHOICE, createGuardController, createUnsavedRegistry, installHistoryGuards,
} from '../../lib/unsavedGuard.js';
import { RR } from '../../lib/router.jsx';

  const { createContext, useContext, useState, useCallback, useRef, useEffect, useMemo } = React;

  const UnsavedContext = createContext(null);
  const ScopeContext = createContext(null);

  /* Диалог. request = { labels, canSave, reason? } | null.
     busy — 'save' (запись из диалога) | 'leave' (идёт сам переход) | false.
     reason — почему переход, если его начал не пользователь в этой вкладке (пространство
     сменили в другой вкладке) — строкой над списком несохранённого.
     saving — у формы идёт СВОЁ сохранение (кнопка в редакторе): кнопки выключены с подписью
     «Сохраняется…», ответить нельзя, пока запись не закончится (круг 3, T3bL: «Отбросить»
     посреди записи давал частичную запись); saved — форма успела сохраниться сама, пока
     диалог был открыт: «Изменения сохранены», вопрос о переходе остаётся. */
  function UnsavedDialog({ request, busy, onChoice, saving = false, saved = false }) {
    if (!request) return null;
    const { labels = [], canSave, reason } = request;
    const locked = !!busy || saving;
    // перечислением, без склонения меток («ТТК «Борщ»», «Сырьё «Лосось»», «Проект меню «…»»)
    const what = saved
      ? 'Изменения сохранены.'
      : (labels.length ? `Не сохранены изменения: ${labels.join(', ')}.` : 'Изменения не сохранены.');
    const body = saved
      ? `${what} Продолжить переход?`
      : (canSave ? `${what} Сохранить их перед переходом?` : `${what} Если продолжить, они будут потеряны.`);
    // своя запись формы — видимая подпись «Сохраняется…» на выключенной кнопке (спиннер
    // .is-loading прячет текст); спиннер — только когда пишет сам диалог
    const saveLoading = !saving && (busy === true || busy === 'save');
    return (
      <Modal open onClose={locked ? undefined : () => onChoice(CHOICE.CANCEL)} size="sm" role="alertdialog"
        zIndex="var(--z-confirm)" className="modal--confirm" labelledBy="unsaved-changes-title"
        closeOnBackdrop={!locked} autoFocusSelector={canSave ? '[data-unsaved-save]' : '[data-safe]'}
        title="Несохранённые изменения"
        footer={<>
          <Button variant="ghost" data-safe disabled={locked} onClick={() => onChoice(CHOICE.CANCEL)}>Отмена</Button>
          <Button variant={canSave ? 'secondary' : 'danger'} disabled={locked} onClick={() => onChoice(CHOICE.DISCARD)}>Отбросить</Button>
          {canSave && (
            <Button variant="primary" data-unsaved-save loading={saveLoading} disabled={locked}
              onClick={() => onChoice(CHOICE.SAVE)}>{saving ? 'Сохраняется…' : 'Сохранить'}</Button>
          )}
        </>}>
        <div className="stack stack-8">
          {reason ? <div className="t-body">{reason}</div> : null}
          <div className="t-body text-dim">{body}</div>
          {saving ? <div className="t-caption text-dim">Идёт сохранение — дождитесь окончания записи.</div> : null}
        </div>
      </Modal>
    );
  }

  const toLoc = (l) => (l ? { pathname: l.pathname || '/', search: l.search || '', state: l.state === undefined ? null : l.state } : null);

  function UnsavedProvider({ children }) {
    const registryRef = useRef(null);
    if (!registryRef.current) registryRef.current = createUnsavedRegistry();
    const [request, setRequest] = useState(null);
    const [busy, setBusy] = useState(false);
    const resolverRef = useRef(null);

    // адрес, на котором стоит приложение (роутер ещё не узнал о popstate) — «откуда»;
    // индекс записи истории — у роутера (RR.currentIndex)
    const location = RR.useLocation();
    const navigate = RR.useNavigate();
    const locRef = useRef(null);
    locRef.current = toLoc(location);
    const navigateRef = useRef(navigate);
    navigateRef.current = navigate;

    const ask = useCallback((req) => new Promise((resolve) => {
      resolverRef.current = resolve;
      setRequest(req);
    }), []);

    const answer = useCallback((choice) => {
      const r = resolverRef.current;
      resolverRef.current = null;
      if (r) r(choice);
    }, []);

    // один вопрос за раз; действие, на которое согласились, — в обход сторожа navigate()
    const ctrlRef = useRef(null);
    if (!ctrlRef.current) {
      ctrlRef.current = createGuardController({
        registry: registryRef.current,
        ask: (req) => ask(req),
        onBusy: (b) => setBusy(b),
        onSettled: () => { setRequest(null); setBusy(false); },
      });
    }
    const guard = ctrlRef.current.guard;

    const register = useCallback((key, isDirty) => {
      registryRef.current.set(key, { dirty: !!isDirty });
    }, []);
    const registerEntry = useCallback((key, entry) => { registryRef.current.set(key, entry); }, []);
    const unregister = useCallback((key) => { registryRef.current.remove(key); }, []);
    const isAnyDirty = useCallback(() => registryRef.current.dirtyKeys().length > 0, []);

    // «назад»/«вперёд» браузера, жест и любой navigate() приложения — через тот же диалог
    useEffect(() => {
      if (typeof window === 'undefined' || !window.history || typeof RR.setPopstateGuard !== 'function') return undefined;
      return installHistoryGuards({
        win: window,
        router: RR,
        registry: registryRef.current,
        controller: ctrlRef.current,
        getAppLocation: () => ({ ...locRef.current, idx: typeof RR.currentIndex === 'function' ? RR.currentIndex() : null }),
        navigateTo: (to) => navigateRef.current(to.pathname + to.search, { replace: true, state: to.state }),
      });
    }, []);

    // beforeunload — браузерное предупреждение при перезагрузке/закрытии вкладки
    useEffect(() => {
      const onBeforeUnload = (e) => {
        if (registryRef.current.dirtyKeys().length) { e.preventDefault(); e.returnValue = ''; }
      };
      window.addEventListener('beforeunload', onBeforeUnload);
      return () => window.removeEventListener('beforeunload', onBeforeUnload);
    }, []);

    const value = useMemo(
      () => ({ register, registerEntry, unregister, guard, isAnyDirty }),
      [register, registerEntry, unregister, guard, isAnyDirty],
    );

    // Пока диалог открыт — следить за формами, о которых он спрашивает: своё сохранение идёт
    // (кнопки выключены) или уже закончилось (форма чистая — «Изменения сохранены»; только до
    // ответа: после «Отбросить» формы тоже чистые, но ничего не сохранено).
    const [, setRegTick] = useState(0);
    useEffect(() => {
      if (!request) return undefined;
      return registryRef.current.subscribe(() => setRegTick((t) => t + 1));
    }, [request]);
    const entries = request ? (request.keys || []).map((k) => registryRef.current.entry(k)).filter(Boolean) : [];
    const saving = entries.some((e) => e.saving);
    const saved = !busy && !saving && entries.length > 0 && entries.every((e) => !e.dirty);
    return (
      <UnsavedContext.Provider value={value}>
        {children}
        <UnsavedDialog request={request} busy={busy} saving={saving} saved={saved} onChoice={answer} />
      </UnsavedContext.Provider>
    );
  }

  // Где смонтирована форма (drawer-стек: верх ?edit на этом пути) — для «назад» браузера.
  function UnsavedScope({ aliveAt, children }) {
    const value = useMemo(() => ({ aliveAt }), [aliveAt]);
    return <ScopeContext.Provider value={value}>{children}</ScopeContext.Provider>;
  }

  // хук формы: грязь + save + метка для диалога; область — из ближайшего UnsavedScope;
  // saving — идёт своё сохранение формы (диалог сторожа на это время выключает кнопки)
  function useUnsavedGuard(key, isDirty, opts) {
    const ctx = useContext(UnsavedContext);
    const scope = useContext(ScopeContext);
    const saveRef = useRef(null);
    saveRef.current = opts && typeof opts.save === 'function' ? opts.save : null;
    const hasSave = !!saveRef.current;
    const label = (opts && opts.label) || null;
    const saving = !!(opts && opts.saving);
    const aliveAt = scope && typeof scope.aliveAt === 'function' ? scope.aliveAt : null;
    useEffect(() => {
      if (!ctx) return;
      if (ctx.registerEntry) {
        ctx.registerEntry(key, {
          dirty: !!isDirty,
          label,
          aliveAt,
          saving,
          save: hasSave ? () => (saveRef.current ? saveRef.current() : false) : null,
        });
      } else {
        ctx.register(key, isDirty);
      }
    }, [ctx, key, isDirty, label, aliveAt, hasSave, saving]);
    useEffect(() => () => {
      if (!ctx) return;
      if (ctx.unregister) ctx.unregister(key); else ctx.register(key, false);
    }, [ctx, key]);
  }

  const useUnsaved = () => useContext(UnsavedContext);

export { UnsavedProvider };
export { UnsavedDialog };
export { UnsavedScope };
export { useUnsaved };
export { useUnsavedGuard };
